import { base64UrlToBytes, bytesToBase64Url, bytesToHex } from './encoding.ts';

// Verificadores de contraseñas y PINs con PBKDF2-SHA256 (WebCrypto nativo).
// Formato: "pbkdf2-sha256$<iteraciones>$<sal base64url>$<hash base64url>"

const SCHEME = 'pbkdf2-sha256';
export const DEFAULT_ITERATIONS = 150_000;
const HASH_BYTES = 32;

async function derive(secret: string, salt: Uint8Array, iterations: number): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(secret),
    'PBKDF2',
    false,
    ['deriveBits'],
  );
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt: salt as BufferSource, iterations },
    key,
    HASH_BYTES * 8,
  );
  return new Uint8Array(bits);
}

export async function createVerifier(
  secret: string,
  options: { iterations?: number; salt?: Uint8Array } = {},
): Promise<string> {
  const iterations = options.iterations ?? DEFAULT_ITERATIONS;
  const salt = options.salt ?? crypto.getRandomValues(new Uint8Array(16));
  const hash = await derive(secret, salt, iterations);
  return `${SCHEME}$${iterations}$${bytesToBase64Url(salt)}$${bytesToBase64Url(hash)}`;
}

export async function verifySecret(secret: string, verifier: string): Promise<boolean> {
  const parts = verifier.split('$');
  if (parts.length !== 4 || parts[0] !== SCHEME) return false;
  const iterations = Number(parts[1]);
  if (!Number.isInteger(iterations) || iterations < 1) return false;
  const salt = base64UrlToBytes(parts[2]!);
  const expected = base64UrlToBytes(parts[3]!);
  const actual = await derive(secret, salt, iterations);
  return timingSafeEqual(actual, expected);
}

export function timingSafeEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a[i]! ^ b[i]!;
  return diff === 0;
}

/** Token aleatorio de alta entropía (p. ej. secreto de dispositivo). */
export function randomToken(bytes = 32): string {
  return bytesToBase64Url(crypto.getRandomValues(new Uint8Array(bytes)));
}

/** SHA-256 en hex. Suficiente para secretos aleatorios de alta entropía (no para PINs). */
export async function sha256Hex(value: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return bytesToHex(new Uint8Array(digest));
}
